import { useCallback, useMemo } from 'react';
import {
  type DefinitionItem,
  useGetBusinessUnitQuery,
  useGetCompanyQuery,
  useGetDepartmentQuery,
  useGetJobQuery,
} from '@/common/graphql';

type GeneralDefinitionItems = Array<Pick<DefinitionItem, 'languageCode' | 'content'>> | undefined;

interface PropsFromQuery {
  type: 'Query';
  companyId: string | null | undefined;
  businessUnitId?: string | null;
  departmentId?: string | null;
  jobId?: string | null;
}

interface PropsFromCached {
  type: 'Cached';
  preferredLanguage: string;
  businessUnitDefinitions?: GeneralDefinitionItems;
  departmentDefinitions?: GeneralDefinitionItems;
  jobDefinitions?: GeneralDefinitionItems;
}

export const useGetGeneralDefinition = (props: PropsFromQuery | PropsFromCached) => {
  const { data: company } = useGetCompanyQuery({
    variables: { id: props.type === 'Query' ? props.companyId! : '' },
    skip: props.type !== 'Query' || !props.companyId,
  });
  const { data: businessUnit } = useGetBusinessUnitQuery({
    variables: { id: props.type === 'Query' ? props.businessUnitId! : '' },
    skip: props.type !== 'Query' || !props.businessUnitId,
  });
  const { data: department } = useGetDepartmentQuery({
    variables: { id: props.type === 'Query' ? props.departmentId! : '' },
    skip: props.type !== 'Query' || !props.departmentId,
  });
  const { data: job } = useGetJobQuery({
    variables: { id: props.type === 'Query' ? props.jobId! : '' },
    skip: props.type !== 'Query' || !props.jobId,
  });

  const getGeneralDefinition = useCallback(
    (definitions: GeneralDefinitionItems) => {
      if (!definitions) return;
      const preferredLanguage = props.type === 'Query' ? company?.company?.preferredLanguage : props.preferredLanguage;

      return (
        definitions.find(it => it.languageCode === (preferredLanguage ?? 'en'))?.content ||
        definitions.find(it => it.languageCode === 'en')?.content
      );
    },
    [company?.company?.preferredLanguage, props],
  );

  const businessUnitDefinitions = useMemo(
    () => (props.type === 'Query' ? businessUnit?.businessUnit?.definitions : props.businessUnitDefinitions),
    [businessUnit?.businessUnit?.definitions, props],
  );
  const departmentDefinitions = useMemo(
    () => (props.type === 'Query' ? department?.department?.definitions : props.departmentDefinitions),
    [department?.department?.definitions, props],
  );
  const jobDefinitions = useMemo(
    () => (props.type === 'Query' ? job?.job?.definitions : props.jobDefinitions),
    [job?.job?.definitions, props],
  );

  const businessUnitDefinition = useMemo(
    () => getGeneralDefinition(businessUnitDefinitions),
    [getGeneralDefinition, businessUnitDefinitions],
  );
  const departmentDefinition = useMemo(
    () => getGeneralDefinition(departmentDefinitions),
    [getGeneralDefinition, departmentDefinitions],
  );
  const jobDefinition = useMemo(() => getGeneralDefinition(jobDefinitions), [getGeneralDefinition, jobDefinitions]);

  return { businessUnitDefinition, departmentDefinition, jobDefinition };
};
